import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Cloud, Database, EyeOff, Gauge, RefreshCw, Server, XCircle, type LucideIcon } from "lucide-react";

import { adminApi } from "../../api/admin";
import { ApiError } from "../../api/client";
import { Badge, Banner, Button, Card, PageHeader, Skeleton, formatDateTime, formatRelative } from "../../ds";
import { errorText } from "./labels";

type State = "ok" | "unset" | "down";

const SERVICES: Record<string, { name: string; icon: LucideIcon; does: string; impact: string; unset?: string }> = {
  api: {
    name: "Reports service",
    icon: Server,
    does: "Runs this website and sends reports to the TV screens.",
    impact: "The website and the TVs can't load new reports until it's back.",
  },
  database: {
    name: "Database",
    icon: Database,
    does: "Stores reports, TV screens, people and the audit log.",
    impact: "Reports can't be opened or saved, and TVs can't fetch updates.",
  },
  redis: {
    name: "Cache & sign-in sessions",
    icon: Gauge,
    does: "Keeps people signed in and makes dashboards load faster.",
    impact: "People may be signed out, and dashboards may load slowly or fail.",
  },
  microsoft_graph: {
    name: "Microsoft 365 connection",
    icon: Cloud,
    does: "Reads SharePoint and Excel files so datasets can refresh.",
    impact: "Data from SharePoint and Excel can't refresh, so dashboards may show old numbers.",
    unset: "The Microsoft 365 app details haven't been entered yet, so SharePoint and Excel data can't be read.",
  },
};
const ORDER = ["api", "database", "redis", "microsoft_graph"];

function stateOf(raw: string): State {
  if (raw === "ok") return "ok";
  if (/not configured/i.test(raw)) return "unset";
  return "down";
}

const STATE_UI: Record<State, { label: string; tone: "success" | "warning" | "danger"; icon: LucideIcon }> = {
  ok: { label: "Working", tone: "success", icon: CheckCircle2 },
  unset: { label: "Not set up", tone: "warning", icon: AlertTriangle },
  down: { label: "Not responding", tone: "danger", icon: XCircle },
};

function humanKey(key: string): string {
  return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function SystemHealthPage() {
  const health = useQuery({ queryKey: ["admin", "health"], queryFn: adminApi.systemHealth, refetchInterval: 30_000 });

  const deps = Object.entries(health.data?.dependencies ?? {}).sort(([a], [b]) => {
    const ia = ORDER.indexOf(a);
    const ib = ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });
  const items = deps.map(([key, raw]) => ({ key, raw: String(raw), state: stateOf(String(raw)) }));
  const down = items.filter((i) => i.state === "down");
  const unset = items.filter((i) => i.state === "unset");
  const checkedAt = health.dataUpdatedAt ? new Date(health.dataUpdatedAt).toISOString() : null;
  const forbidden = health.error instanceof ApiError && health.error.status === 403;
  const nameOf = (key: string) => SERVICES[key]?.name ?? humanKey(key);

  return (
    <div className="ds-page">
      <PageHeader
        title="System health"
        subtitle="Live checks of the services the platform depends on. They run again automatically every 30 seconds."
        actions={
          <Button icon={RefreshCw} loading={health.isFetching} onClick={() => void health.refetch()}>
            Check again
          </Button>
        }
      />

      <div className="ds-stack" style={{ gap: 20 }}>
        {health.isError ? (
          forbidden ? (
            <Banner tone="warning" title="Only platform admins can see system health">
              Ask a platform admin if you need to know whether a service is down.
            </Banner>
          ) : (
            <Banner tone="danger" title="Couldn't reach the platform" action={<Button size="sm" icon={RefreshCw} onClick={() => void health.refetch()}>Try again</Button>}>
              The health check itself didn't answer, which usually means the reports service is down or your connection dropped. {errorText(health.error, "")}
            </Banner>
          )
        ) : health.isLoading ? (
          <Skeleton height={56} />
        ) : down.length ? (
          <Banner tone="danger" title={`${down.length === 1 ? "1 service isn't" : `${down.length} services aren't`} responding`}>
            {down.map((i) => nameOf(i.key)).join(", ")} — see below for what this affects.
            {unset.length ? ` ${unset.map((i) => nameOf(i.key)).join(", ")} ${unset.length === 1 ? "isn't" : "aren't"} set up yet.` : ""} Checked {formatRelative(checkedAt)}.
          </Banner>
        ) : unset.length ? (
          <Banner tone="warning" title="Everything that's set up is working">
            {unset.map((i) => nameOf(i.key)).join(", ")} {unset.length === 1 ? "isn't" : "aren't"} set up yet. Checked {formatRelative(checkedAt)}.
          </Banner>
        ) : (
          <Banner tone="success" title="Everything is working">
            All {items.length} services answered normally. Checked {formatRelative(checkedAt)}.
          </Banner>
        )}

        {health.isLoading ? (
          <div className="ds-grid ds-grid--2">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i}>
                <div className="ds-row" style={{ gap: 14 }}>
                  <Skeleton width={42} height={42} />
                  <div className="ds-grow">
                    <Skeleton width="45%" height={16} />
                    <Skeleton width="85%" style={{ marginTop: 10 }} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : health.isSuccess ? (
          <div className="ds-grid ds-grid--2">
            {items.map(({ key, raw, state }) => {
              const svc = SERVICES[key];
              const ui = STATE_UI[state];
              const Icon = svc?.icon ?? Server;
              return (
                <Card key={key}>
                  <div className="ds-row" style={{ gap: 14, alignItems: "flex-start" }}>
                    <span className={`ds-stat__icon ds-stat__icon--${ui.tone}`}>
                      <Icon size={20} />
                    </span>
                    <div className="ds-grow" style={{ minWidth: 0 }}>
                      <div className="ds-row" style={{ justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 650, fontSize: 15 }}>{nameOf(key)}</div>
                        <Badge tone={ui.tone}>
                          <ui.icon size={12} /> {ui.label}
                        </Badge>
                      </div>
                      <p className="ds-muted" style={{ margin: "4px 0 0" }}>
                        {svc?.does ?? "A service the platform depends on."}
                      </p>
                      {state !== "ok" ? (
                        <div style={{ marginTop: 10 }}>
                          <p className="ds-small" style={{ margin: 0 }}>
                            <strong>What this means: </strong>
                            {state === "unset" ? svc?.unset ?? "This service hasn't been set up yet." : svc?.impact ?? "Parts of the platform may not work until it's back."}
                          </p>
                          <p className="ds-small ds-faint ds-mono" style={{ margin: "6px 0 0", wordBreak: "break-word" }} title="Technical detail for IT">
                            {raw}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}

        {health.isSuccess ? (
          <div className="ds-row ds-small ds-faint" style={{ gap: 8, alignItems: "flex-start" }}>
            <EyeOff size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Not checked here yet: background data refreshes and file storage. Failed refreshes show up on the Home page and in Datasets.
              {checkedAt ? ` Last check: ${formatDateTime(checkedAt)}.` : ""}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
